<template>
  <div>
    <!-- retain-focus off: browser extensions (e.g. Grammarly) that inject
         focusable overlays otherwise cause an infinite focus loop -->
    <v-dialog v-model="dialog" persistent :retain-focus="false" width="1200">
      <v-card>
        <ModalHeader
          title="Down Payments Modal"
          icon="mdi-cash"
          @close="dialog = false"
        />
        <v-card-text class="pa-4">
          <!-- Access Control Alert -->
          <v-alert v-if="!hasAccess" type="error" prominent>
            <strong>Access Denied!</strong>
            <div>
              You don't have permission to manage down payments. Only Admin,
              Bookkeeper, and Staff can access this feature.
            </div>
          </v-alert>

          <div v-if="hasAccess">
            <!-- add downpayment card here -->
            <v-row>
              <v-col>
                <div>
                  <v-text-field
                    outlined
                    dense
                    label="Search Customer"
                    v-model="searchCustomer"
                  ></v-text-field>
                  <v-data-table
                    :items="customers"
                    :headers="customer_headers"
                    :search="searchCustomer"
                    :items-per-page="5"
                  >
                    <template v-slot:item.action="{ item }">
                      <div class="d-flex">
                        <v-btn
                          small
                          @click="selectCustomer(item)"
                          color="primary"
                          ><v-icon>mdi-check</v-icon>Select
                        </v-btn>
                      </div>
                    </template>
                  </v-data-table>
                </div>
              </v-col>
              <v-col>
                <h3>Selected: {{ selectedCustomer.customer_name }}</h3>
                <v-text-field
                  outlined
                  dense
                  label="Amount Paid"
                  type="number"
                  v-model="form.amount_paid"
                  class="mr-2"
                  prepend-icon="mdi-cash"
                ></v-text-field>
                <v-text-field
                  outlined
                  dense
                  label="Description"
                  v-model="form.description"
                  prepend-icon="mdi-pen"
                ></v-text-field>
                <div class="d-flex">
                  <p>Date Paid</p>
                  <input type="date" v-model="form.paymentDate" />
                </div>
                <v-select
                  outlined
                  dense
                  label="Payment Method"
                  :items="chart_of_accounts"
                  item-text="account_title"
                  item-value="id"
                  v-model="form.paymentMethodId"
                ></v-select>

                <v-btn color="primary" @click="submit" :loading="loading"
                  >Submit</v-btn
                >
              </v-col>
            </v-row>
            <br />
            <h3 class="display-1">Downpayments</h3>
            <div class="d-flex">
              <v-text-field
                outlined
                dense
                label="search"
                v-model="search"
                prepend-inner-icon="mdi-magnify"
              ></v-text-field>
              <v-btn color="success" @click="refresh()"
                ><v-icon>mdi-refresh</v-icon>Refresh
              </v-btn>
            </div>
            <v-data-table
              :search="search"
              :items="downpayments"
              :headers="headers"
            >
              <template v-slot:item.status="{ item }">
                <v-chip
                  small
                  :color="
                    item.status === 'Transacted'
                      ? 'green'
                      : item.status === 'Partial'
                      ? 'orange'
                      : item.status === 'Cancelled'
                      ? 'red'
                      : 'blue-grey'
                  "
                  dark
                >
                  {{ item.status || "Pending" }}
                </v-chip>
              </template>
              <!-- add action column with print and delete function -->
              <template v-slot:item.action="{ item }">
                <div class="d-flex">
                  <v-btn
                    small
                    icon
                    color="success"
                    class="mt-1"
                    @click="print(item)"
                    ><v-icon>mdi-printer</v-icon></v-btn
                  >
                  <v-btn
                    v-if="!item.status || item.status === 'Pending'"
                    small
                    icon
                    color="orange"
                    class="mt-1"
                    @click="cancel(item)"
                    ><v-icon>mdi-cancel</v-icon></v-btn
                  >
                  <v-btn small icon color="red" class="mt-1" @click="del(item)"
                    ><v-icon>mdi-delete</v-icon></v-btn
                  >
                </div>
              </template>
            </v-data-table>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-btn color="primary" @click="submit" :loading="loading"
            >Submit</v-btn
          >
          <v-btn @click="dialog = false"> Close</v-btn>
          <v-spacer></v-spacer>
          <p class="caption grey--text">itemProfileModal.vue</p>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import moment from "moment";
import axios from "axios";
import { mapGetters, mapActions } from "vuex/dist/vuex.common.js";
export default {
  data() {
    return {
      search: "",
      dialog: false,
      loading: false,
      headers: [
        { text: "Action", value: "action", width: "80" },
        { text: "ID", value: "id" },
        { text: "Reference Number", value: "reference_number" },
        { text: "Customer Name", value: "customer.customer_name" },
        { text: "Payment Method", value: "chart_of_account.account_title" },
        { text: "Amount", value: "amount_paid" },
        { text: "Available", value: "available_amount" },
        { text: "Status", value: "status" },
        { text: "Date", value: "payment_date" },
        { text: "Remarks", value: "description" },
      ],
      customer_headers: [
        { text: "", value: "action", width: "80" },
        { text: "ID", value: "id" },
        {
          text: "Customer Name",
          value: "customer_name",
        },
      ],
      searchCustomer: "",
      downpayments: [],
      selectedCustomer: {},
      form: {},
    };
  },
  computed: {
    hasAccess() {
      // Gated purely by the down_payments.access permission key (hat bundle /
      // Role Restrictions); accounts without hats get no access.
      return this.hasPermission("down_payments.access");
    },
    ...mapGetters({
      user: "auth/user",
      user_permissions: "permission/user_permissions",
      hasPermission: "permission/hasPermission",
      customers: "customer/customers",
      chart_of_accounts: "chart_of_account/chart_of_accounts",
      default_server_url: "auth/default_server_url",
    }),
  },
  methods: {
    async del(item) {
      // A downpayment that has already been applied to an invoice cannot be
      // deleted, otherwise the linked payment would be left dangling.
      if (item.status && item.status !== "Pending") {
        this.$alert({
          title: "Cannot Delete",
          message:
            "This down payment has already been used as payment (" +
            item.status +
            "). Remove the related invoice payment first.",
          color: "warning",
          icon: "mdi-alert",
        });
        return;
      }
      let x = await this.$confirm({
        title: "Delete Downpayment",
        message: "Are you sure you want to delete this downpayment?",
        color: "red",
        icon: "mdi-delete",
      });
      if (x) {
        console.log(item.id);
        let response = await axios.delete("/downpayment/" + item.id);
        this.$alert({
          title: "Success",
          message: response.data.message,
          color: "success",
          icon: "mdi-check-circle",
        });
        this.getDownPayments();
      }
    },
    async cancel(item) {
      // Same rule as delete: once part of the downpayment funds an invoice
      // payment, the linked payment must be removed before cancelling.
      if (item.status && item.status !== "Pending") {
        this.$alert({
          title: "Cannot Cancel",
          message:
            "This down payment has already been used as payment (" +
            item.status +
            "). Remove the related invoice payment first.",
          color: "warning",
          icon: "mdi-alert",
        });
        return;
      }
      let x = await this.$confirm({
        title: "Cancel Downpayment",
        message:
          "This will post reversing journal entries, mark the downpayment as Cancelled, and notify the customer via SMS. Proceed?",
        color: "orange",
        icon: "mdi-cancel",
      });
      if (!x) return;
      try {
        let response = await axios.post("/downpayment/" + item.id + "/cancel");
        let message = response.data.message;
        const sms =
          response.data.downpayment &&
          response.data.downpayment.sms_notification;
        if (sms) {
          message +=
            sms.status === "sent"
              ? " SMS sent to " + sms.recipient + "."
              : " SMS not sent (" + (sms.error || sms.status) + ").";
        }
        this.$alert({
          title: "Downpayment Cancelled",
          message: message,
          color: "success",
          icon: "mdi-check-circle",
        });
        this.getDownPayments();
      } catch (e) {
        this.$alert({
          title: "Error",
          message:
            (e.response && e.response.data && e.response.data.message) ||
            e.message,
          color: "error",
          icon: "mdi-alert-circle",
        });
      }
    },
    refresh() {
      this.loading = true;
      this.getDownPayments().then(() => {
        this.loading = false;
      });
      return;
    },
    ...mapActions({
      get_chart_of_accounts: "chart_of_account/get_chart_of_accounts",
    }),
    selectCustomer(item) {
      this.selectedCustomer = item;
      this.form.customer_id = this.selectedCustomer.id;
    },
    print(item) {
      const {
        generateDownpaymentReceipt,
      } = require("@/utils/dotMatrixPrinter");
      const printer = generateDownpaymentReceipt(item, this.user);
      printer.print({ copies: 2 });
    },
    async submit() {
      // Check access before submitting
      if (!this.hasAccess) {
        this.$alert({
          title: "Error",
          message:
            "Access Denied! You don't have permission to create down payments.",
          color: "error",
          icon: "mdi-alert-circle",
        });
        return;
      }

      //check if fields are complete
      if (
        !this.form.customer_id ||
        !this.form.amount_paid ||
        !this.form.description ||
        !this.form.paymentDate ||
        !this.form.paymentMethodId
      ) {
        this.$alert({
          title: "Validation",
          message: "Please fill in all fields.",
          color: "warning",
          icon: "mdi-alert",
        });
        return;
      } else {
        let x = await this.$confirm({
          title: "Add Downpayment",
          message: "Are you sure you want to proceed?",
          color: "primary",
          icon: "mdi-help-circle-outline",
        });
        if (x) {
          this.loading = true;
          let response = await axios
            .post("/downpayment", this.form)
            .catch((error) => {
              this.$alert({
                title: "Error",
                message: "Error adding downpayment: " + error.message,
                color: "error",
                icon: "mdi-alert-circle",
              });
            });

          this.$alert({
            title: "Success",
            message:
              "Downpayment added successfully with Reference Number: " +
              response.data.reference_number,
            color: "success",
            icon: "mdi-check-circle",
          });
          this.downpayments.unshift(response.data);

          // Ask whether to print the Down Payment AR Slip.
          let wantsPrint = await this.$confirm({
            title: "Print AR Slip?",
            message:
              "Would you like to print the Down Payment AR Slip for this transaction?",
            confirmText: "Yes, Print",
            cancelText: "No",
            color: "primary",
            icon: "mdi-printer",
          });
          if (wantsPrint) {
            this.print(response.data);
          }

          this.form = {}; // Clear form
          this.form.paymentDate = moment().format("YYYY-MM-DD");
          this.selectedCustomer = {}; // Clear selected customer
          this.loading = false;
        }
      }

      console.log(this.form);
      return;
    },
    async getDownPayments() {
      let response = await axios.get("/downpayments");
      this.downpayments = response.data;
      console.log(this.downpayments);
    },
  },
  created() {
    this.getDownPayments();
    if (this.chart_of_accounts <= 0) this.get_chart_of_accounts();
    //set defaul payment date to current date using moment
    this.form.paymentDate = moment().format("YYYY-MM-DD");
  },
};
</script>

<style lang="scss" scoped></style>
